// src/services/seasonViewService.ts
import apiClient from '../utils/apiClient';

export interface SeasonService {
  service_id: number;
  liturgy_date: string;
  time: string;
  liturgy_name: string | null;
  church_id: number;
  service_def_id: number | null;
  ot_reading: string | null;
  nt_reading: string | null;
  gospel_reading: string | null;
  psalm_reading: string | null;
  song_ideas: any[];
  season: string | null;
  is_special: boolean | null;
}

export interface SeasonSelection {
  service_selection_id: number;
  service_id: number;
  song_id: number | null;
  order_number: number;
  placement: string | null;
  performance_type: string | null;
  is_template_music: boolean;
  director_notes: string | null;
  at_organ: boolean;
  song_title: string;
  composer_name: string | null;
  tune_name: string | null;
  has_file: boolean;
}

export interface SeasonSpanishSelection {
  spanish_song_selection_id: number;
  service_id: number;
  placement: string | null;
  song_title: string;
  order_number: number;
}

export interface OrganWork {
  id: number;
  service_id: number;
  slot: string;
  work: string;
  composer: string | null;
  duration: string | null;
  notes: string | null;
  order_number: number;
}

export type MusicianStatus = 'idea' | 'asked' | 'accepted' | 'confirmed' | 'declined' | 'inhouse';

export interface Musician {
  id: number;
  service_id: number;
  name: string;
  instrument: string | null;
  status: MusicianStatus;
  note: string | null;
  order_number: number;
}

export interface SeasonData {
  services: SeasonService[];
  selections: SeasonSelection[];
  spanishSelections: SeasonSpanishSelection[];
  organ: OrganWork[];
  musicians: Musician[];
}

export const fetchSeason = async (
  startDate: string,
  endDate: string,
  churchIds?: number[]
): Promise<SeasonData> => {
  const params: any = { startDate, endDate };
  if (churchIds && churchIds.length > 0) params.churchIds = churchIds.join(',');
  const { data } = await apiClient.get<SeasonData>('/season-view', { params });
  return data;
};

export const fetchMusicianSuggestions = async (): Promise<
  { name: string; instrument: string | null; times: number }[]
> => {
  const { data } = await apiClient.get('/season-view/musician-suggestions');
  return data;
};

export const addOrganWork = async (serviceId: number, body: Partial<OrganWork>): Promise<OrganWork> =>
  (await apiClient.post(`/season-view/services/${serviceId}/organ`, body)).data;

export const updateOrganWork = async (id: number, body: Partial<OrganWork>): Promise<OrganWork> =>
  (await apiClient.put(`/season-view/organ/${id}`, body)).data;

export const deleteOrganWork = async (id: number): Promise<void> => {
  await apiClient.delete(`/season-view/organ/${id}`);
};

export const addMusician = async (serviceId: number, body: Partial<Musician>): Promise<Musician> =>
  (await apiClient.post(`/season-view/services/${serviceId}/musicians`, body)).data;

export const updateMusician = async (id: number, body: Partial<Musician>): Promise<Musician> =>
  (await apiClient.put(`/season-view/musicians/${id}`, body)).data;

export const deleteMusician = async (id: number): Promise<void> => {
  await apiClient.delete(`/season-view/musicians/${id}`);
};

export const setAtOrgan = async (selectionId: number, atOrgan: boolean): Promise<void> => {
  await apiClient.put(`/season-view/selections/${selectionId}/at-organ`, { at_organ: atOrgan });
};
